import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertCircle, RotateCcw, X } from "lucide-react";

/* Detects a stalled SocialPulse SSO flow. We stash a short "sp_sso_pending"
   marker in sessionStorage when the user taps "Continue with SocialPulse".
   If the hub (socialpulse.com) completes Google auth but forgets to honor
   our oidc_return and leaves the user on its own homepage, they manually
   navigate back to travelerpulse.pro. The marker is still present (we only
   clear it on successful callback) — show a banner that resumes the flow in
   one tap. The banner never renders during the callback itself. */
export default function SsoRecoveryBanner() {
  const { pathname } = useLocation();
  const [stale, setStale] = useState(null);

  const check = () => {
    try {
      const raw = sessionStorage.getItem("sp_sso_pending");
      if (!raw) { setStale(null); return; }
      const info = JSON.parse(raw);
      // Hide the banner on the actual callback / login pages themselves.
      if (pathname.startsWith("/auth/")) { setStale(null); return; }
      // Treat as stale after 20 seconds — enough time for a legit redirect
      // round-trip, long enough that we're not fighting a slow flow.
      const age = Date.now() - (info.started_at || 0);
      setStale(age > 20000 ? info : null);
    } catch { setStale(null); }
  };

  useEffect(() => {
    check();
    const iv = setInterval(check, 4000);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = () => {
    const returnTo = stale?.return_to || "/";
    sessionStorage.removeItem("sp_sso_pending");
    window.location.assign(`/api/auth/socialpulse/init?return_to=${encodeURIComponent(returnTo)}`);
  };

  const dismiss = () => {
    sessionStorage.removeItem("sp_sso_pending");
    setStale(null);
  };

  if (!stale) return null;

  return (
    <div className="fixed top-16 left-3 right-3 sm:left-auto sm:right-5 sm:max-w-md z-50 bg-amber-50 border-2 border-amber-300 rounded-2xl shadow-lg p-4 flex items-start gap-3"
      data-testid="sso-recovery-banner">
      <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-700 text-sm text-amber-900">Finish signing in with SocialPulse</div>
        <p className="text-xs text-amber-900/80 mt-0.5">
          Looks like SocialPulse signed you in but didn't send you back. Tap <b>Resume</b> to finish — we'll take you straight to where you were.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={retry} data-testid="sso-recovery-retry"
            className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white rounded-full px-3 py-1.5 text-xs font-700">
            <RotateCcw className="w-3.5 h-3.5" /> Resume sign-in
          </button>
          <button onClick={dismiss} data-testid="sso-recovery-dismiss"
            className="text-xs font-600 text-amber-900/70 hover:text-amber-900 px-2 py-1.5">
            Dismiss
          </button>
        </div>
      </div>
      <button onClick={dismiss} aria-label="Close" className="text-amber-700/60 hover:text-amber-900 p-1">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
